<template>
    <div
        class="d-inline-flex flex-column"
        role="group"
        :aria-disabled="disabled ? 'true' : null"
        :aria-label="$t('Selected time')">
        <div class="sr-only" role="status" aria-live="polite" aria-atomic="true">
            {{ formattedTimeString }}
        </div>
        <div
            class="time-picker-columns d-flex align-items-center justify-content-center mx-auto"
            role="group"
            tabindex="-1">
            <div
                class="time-picker-spinbutton form-control d-inline-flex flex-column align-items-center time-picker-hours"
                :class="{disabled}"
                role="group"
                :aria-label="$t('Hours')">
                <button
                    type="button"
                    class="btn btn-sm border-0 rounded-0 d-flex align-items-center justify-content-center w-100"
                    :disabled="disabled"
                    tabindex="-1"
                    :aria-label="$t('Increase')"
                    @click="incrementHours">
                    <ChevronUpIcon class="text-primary"/>
                </button>
                <output
                    class="flex-grow-1 d-flex align-items-center justify-content-center border-top border-bottom time-picker-value"
                    role="spinbutton"
                    data-spin="hours"
                    :tabindex="disabled ? -1 : 0"
                    :aria-label="$t('Hours')"
                    :aria-valuenow="hours === null ? null : hours"
                    :aria-valuemin="0"
                    :aria-valuemax="23"
                    :aria-valuetext="formattedHours"
                    @keydown="onSpinKeydown($event, incrementHours, decrementHours)">
                    <bdi>{{ formattedHours }}</bdi>
                </output>
                <button
                    type="button"
                    class="btn btn-sm border-0 rounded-0 d-flex align-items-center justify-content-center w-100"
                    :disabled="disabled"
                    tabindex="-1"
                    :aria-label="$t('Decrease')"
                    @click="decrementHours">
                    <ChevronDownIcon class="text-primary"/>
                </button>
            </div>
            <span
                class="time-picker-colon display-2 font-weight-bold px-1"
                aria-hidden="true">
                :
            </span>
            <div
                class="time-picker-spinbutton form-control d-inline-flex flex-column align-items-center time-picker-minutes"
                :class="{disabled}"
                role="group"
                :aria-label="$t('Minutes')">
                <button
                    type="button"
                    class="btn btn-sm border-0 rounded-0 d-flex align-items-center justify-content-center w-100"
                    :disabled="disabled"
                    tabindex="-1"
                    :aria-label="$t('Increase')"
                    @click="incrementMinutes">
                    <ChevronUpIcon class="text-primary"/>
                </button>
                <output
                    class="flex-grow-1 d-flex align-items-center justify-content-center border-top border-bottom time-picker-value"
                    role="spinbutton"
                    data-spin="minutes"
                    :tabindex="disabled ? -1 : 0"
                    :aria-label="$t('Minutes')"
                    :aria-valuenow="minutes === null ? null : minutes"
                    :aria-valuemin="0"
                    :aria-valuemax="59"
                    :aria-valuetext="formattedMinutes"
                    @keydown="onSpinKeydown($event, incrementMinutes, decrementMinutes)">
                    <bdi>{{ formattedMinutes }}</bdi>
                </output>
                <button
                    type="button"
                    class="btn btn-sm border-0 rounded-0 d-flex align-items-center justify-content-center w-100"
                    :disabled="disabled"
                    tabindex="-1"
                    :aria-label="$t('Decrease')"
                    @click="decrementMinutes">
                    <ChevronDownIcon class="text-primary"/>
                </button>
            </div>
            <div
                v-if="is12Hour"
                class="time-picker-spinbutton form-control d-inline-flex flex-column align-items-center ml-2"
                :class="{disabled}"
                role="group"
                :aria-label="$t('AM') + '/' + $t('PM')">
                <button
                    type="button"
                    class="btn btn-sm border-0 rounded-0 d-flex align-items-center justify-content-center w-100"
                    :disabled="disabled"
                    tabindex="-1"
                    :aria-label="$t('AM') + '/' + $t('PM')"
                    @click="toggleMeridiem">
                    <ChevronUpIcon class="text-primary"/>
                </button>
                <output
                    class="flex-grow-1 d-flex align-items-center justify-content-center border-top border-bottom time-picker-value"
                    role="spinbutton"
                    data-spin="meridiem"
                    :tabindex="disabled ? -1 : 0"
                    :aria-label="$t('AM') + '/' + $t('PM')"
                    :aria-valuetext="meridiem"
                    @keydown="onSpinKeydown($event, toggleMeridiem, toggleMeridiem)">
                    <bdi>{{ meridiem }}</bdi>
                </output>
                <button
                    type="button"
                    class="btn btn-sm border-0 rounded-0 d-flex align-items-center justify-content-center w-100"
                    :disabled="disabled"
                    tabindex="-1"
                    :aria-label="$t('AM') + '/' + $t('PM')"
                    @click="toggleMeridiem">
                    <ChevronDownIcon class="text-primary"/>
                </button>
            </div>
        </div>
        <footer v-if="reset && timeValue" class="mt-2 d-flex justify-content-center">
            <button
                type="button"
                class="btn btn-danger btn-sm"
                @click="clearTime">
                {{ $t('Clear time') }}
            </button>
        </footer>
    </div>
</template>

<script>
import {mapGetters} from 'vuex';
import ChevronUpIcon from '@/common/components/svg-icons/ChevronUpIcon.vue';
import ChevronDownIcon from '@/common/components/svg-icons/ChevronDownIcon.vue';

export default {
    name: 'TimePickerPanel',
    props: {
        modelValue: {
            type: String,
            default: undefined
        },
        value: { // TODO: Vue3 remove
            type: String,
            default: undefined
        },
        minutesStep: {
            type: Number,
            default: 1
        },
        disabled: {
            type: Boolean,
            default: false
        },
        reset: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            hours: null,
            minutes: null
        };
    },
    computed: {
        ...mapGetters(['language']),
        boundValue() {
            if (this.modelValue !== undefined) {
                return this.modelValue;
            }
            if (this.value !== undefined) { // TODO: Vue3 remove
                return this.value;
            }
            return '';
        },
        is12Hour() {
            if (!this.language) {
                return false;
            }
            try {
                const options = new Intl.DateTimeFormat(this.language, {hour: 'numeric'}).resolvedOptions();
                return Boolean(options.hour12);
            } catch (e) {
                return false;
            }
        },
        meridiem() {
            if (this.hours === null) {
                return '';
            }
            return this.hours >= 12 ? this.$t('PM') : this.$t('AM');
        },
        formattedHours() {
            if (this.hours === null) {
                return '--';
            }
            if (this.is12Hour) {
                const twelveHour = this.hours % 12 === 0 ? 12 : this.hours % 12;
                return String(twelveHour).padStart(2, '0');
            }
            return String(this.hours).padStart(2, '0');
        },
        formattedMinutes() {
            if (this.minutes === null) {
                return '--';
            }
            return String(this.minutes).padStart(2, '0');
        },
        timeValue() {
            if (this.hours === null || this.minutes === null
                || Number.isNaN(this.hours) || Number.isNaN(this.minutes)) {
                return '';
            }
            return `${String(this.hours).padStart(2, '0')}:${String(this.minutes).padStart(2, '0')}`;
        },
        formattedTimeString() {
            if (!this.timeValue) {
                return this.$t('No time selected');
            }
            if (this.is12Hour) {
                return `${this.formattedHours}:${this.formattedMinutes} ${this.meridiem}`;
            }
            return this.timeValue;
        }
    },
    watch: {
        boundValue: {
            immediate: true,
            handler(newValue) {
                this.syncFromValue(newValue);
            }
        }
    },
    methods: {
        parseTime(value) {
            if (!value || typeof value !== 'string') {
                return {hours: null, minutes: null};
            }
            const match = value.match(/^(\d{1,2}):(\d{1,2})/);
            if (!match) {
                return {hours: null, minutes: null};
            }
            return {
                hours: Math.max(0, Math.min(23, parseInt(match[1], 10))),
                minutes: Math.max(0, Math.min(59, parseInt(match[2], 10)))
            };
        },
        snapToStep(value, step, min, max) {
            const steps = Math.floor((max - min) / step) + 1;
            const stepIndex = Math.round((value - min) / step);
            const clampedIndex = Math.max(0, Math.min(steps - 1, stepIndex));
            return min + clampedIndex * step;
        },
        stepWrap(value, delta, step, min, max) {
            const steps = Math.floor((max - min) / step) + 1;
            const currentIndex = Math.round((value - min) / step);
            let nextIndex = currentIndex + delta;
            if (nextIndex >= steps) {
                nextIndex = 0;
            } else if (nextIndex < 0) {
                nextIndex = steps - 1;
            }
            return min + nextIndex * step;
        },
        syncFromValue(value) {
            const parsed = this.parseTime(value);
            if (parsed.hours === this.hours && parsed.minutes === this.minutes) {
                return;
            }
            this.hours = parsed.hours;
            this.minutes = parsed.minutes === null
                ? null
                : this.snapToStep(parsed.minutes, this.minutesStep, 0, 59);
        },
        emitInput() {
            const value = this.timeValue;
            if (value !== this.boundValue) {
                this.$emit('update:modelValue', value);
                this.$emit('input', value); // TODO: Vue3 remove
            }
        },
        ensureDefaults() {
            if (this.hours === null) {
                this.hours = 12;
            }
            if (this.minutes === null) {
                this.minutes = 0;
            }
        },
        mutateTime(mutator) {
            if (this.disabled) {
                return;
            }
            this.ensureDefaults();
            mutator();
            this.emitInput();
        },
        incrementHours() {
            this.mutateTime(() => {
                this.hours = this.stepWrap(this.hours, 1, 1, 0, 23);
            });
        },
        decrementHours() {
            this.mutateTime(() => {
                this.hours = this.stepWrap(this.hours, -1, 1, 0, 23);
            });
        },
        incrementMinutes() {
            this.mutateTime(() => {
                this.minutes = this.stepWrap(this.minutes, 1, this.minutesStep, 0, 59);
            });
        },
        decrementMinutes() {
            this.mutateTime(() => {
                this.minutes = this.stepWrap(this.minutes, -1, this.minutesStep, 0, 59);
            });
        },
        toggleMeridiem() {
            this.mutateTime(() => {
                this.hours = this.hours >= 12 ? this.hours - 12 : this.hours + 12;
            });
        },
        onSpinKeydown(event, onUp, onDown) {
            if (this.disabled) {
                return;
            }
            if (event.key === 'ArrowUp') {
                event.preventDefault();
                onUp();
            } else if (event.key === 'ArrowDown') {
                event.preventDefault();
                onDown();
            }
        },
        focusHours() {
            const el = this.$el.querySelector('[data-spin="hours"]');
            if (el && typeof el.focus === 'function') {
                el.focus();
            }
        },
        clearTime() {
            this.hours = null;
            this.minutes = null;
            this.$emit('update:modelValue', '');
            this.$emit('input', ''); // TODO: Vue3 remove
        }
    },
    components: {
        ChevronUpIcon,
        ChevronDownIcon
    }
};
</script>

<style lang="scss" scoped>
@import "@common/styles/variables/_variables.scss";

.time-picker-columns {
    [dir="rtl"] & {
        flex-direction: row-reverse;
    }
}

.time-picker-colon {
    margin-top: calc((1cap - 1ex) / -2);
}

.time-picker-spinbutton {
    text-align: center;
    overflow: hidden;
    background-image: none;
    padding: 0;
    height: auto;
    width: auto;

    .time-picker-value {
        font-size: inherit;
        background-color: transparent;
        width: auto;
        margin: 0 0.25rem;
        padding: 0.25rem 0;
        outline: none;
        cursor: ns-resize;

        > bdi {
            display: block;
            min-width: 2.25em;
            height: 1em * $line-height-base;
        }
    }

    .btn {
        line-height: 1;
        box-shadow: none !important;

        &:disabled {
            pointer-events: none;
        }
    }

    &.disabled {
        background-color: $gray-200;
        pointer-events: none;
    }

    &:focus-within {
        border-color: $primary;
        border-radius: 0.25rem;
        z-index: 1;

        .time-picker-value {
            border-color: $primary !important;
        }
    }
}
</style>
